import React, { useRef, useMemo } from 'react';
import { styled } from '@compiled/react';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import Button from '@atlaskit/button';

import { PopupSelect } from '@atlaskit/select';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useSelectedWeekStore } from '../../controllers/selected-date-range/index.tsx';
import { useReportCoreData } from '../../services/deployment-frequency-core/index.tsx';
import { getProdDataState } from '../../services/deployment-frequency-core/utils.tsx';
import { LoadingSkeleton } from './loading-skeleton/index.tsx';
import messages from './messages.tsx';
import { getDateOptionsFromCoreData } from './utils.tsx';

const PeriodFilter = () => {
	const [{ selectedWeekStart }, { setSelectedWeekStart }] = useSelectedWeekStore();
	const { data: coreData, loading: coreDataLoading, error: coreDataError } = useReportCoreData();

	const { formatMessage, formatDate } = useIntl();
	const hasReportedError = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const weekStartDates = useMemo(
		() => (coreData ? getDateOptionsFromCoreData(coreData) : []),
		[coreData],
	);
	const options = useMemo(
		() =>
			coreData
				? [
						{
							label: formatMessage(messages.weekCommencing),
							options: weekStartDates.map((singleBarData) => ({
								label: formatDate(singleBarData.startDateInclusive, {
									month: 'short',
									day: 'numeric',
								}),
								value: singleBarData.startDateInclusive,
							})),
						},
					]
				: [],
		[coreData, weekStartDates, formatDate, formatMessage],
	);

	if (coreDataError && !hasReportedError.current) {
		hasReportedError.current = true;
		fireErrorAnalytics({
			error: coreDataError,
			meta: {
				id: 'periodFilterAGGError',
				packageName: 'jiraSoftwareDeploymentFrequencyReport',
				teamName: 'fusion-solaris',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	}

	if (coreDataLoading || selectedWeekStart === null || coreData == null) {
		return <LoadingSkeleton />;
	}

	const prodDataState = getProdDataState(coreData);
	return (
		<Wrapper>
			<div data-test-id="software-deployment-frequency-report.ui.period-filter.div">
				<PopupSelect
					classNamePrefix="period-filter-popup-select"
					searchThreshold={100}
					options={options}
					value={options[0].options.find((o) => o.value.getTime() === selectedWeekStart.getTime())}
					// @ts-expect-error - TS2339 - Property 'label' does not exist on type '{ label: string; value: Date; } | null'. | TS2339 - Property 'value' does not exist on type '{ label: string; value: Date; } | null'.
					onChange={({ label, value }, { action }) => {
						if (action === 'select-option') {
							fireUIAnalytics(
								createAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'selectedWeek',
									actionSubjectId: 'deploymentFrequencyReport',
								}),
							);

							const singleBarData = weekStartDates.find((x) => x.startDateInclusive === value);
							if (!singleBarData) {
								throw new Error(`cannot find dateRange from ${label}`);
							}
							setSelectedWeekStart(singleBarData.startDateInclusive);
						}
					}}
					target={({ isOpen, ...triggerProps }) => (
						<Button
							{...triggerProps}
							isSelected={isOpen}
							iconAfter={<ChevronDownIcon label="" size="small" />}
							isDisabled={prodDataState === 'COMPLETELY_EMPTY'}
						>
							{formatMessage(messages.weekOf, {
								date: formatDate(selectedWeekStart, {
									month: 'short',
									day: 'numeric',
								}),
							})}
						</Button>
					)}
				/>
			</div>
		</Wrapper>
	);
};

export default PeriodFilter;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});
